import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import JobListData from './ScheduleNewJobList';

interface FlaggedJobsProps {
    scheduleDate: string;
    dateFormatted: string;
    systemCompanyId: number;
    scheduleBranchId: number;
    showModal: boolean;
    hideModal(): void;
    save(datesToUpdate: string[]): void;
    filterDate(filterDate: string, scheduleId: number): void;
}

interface Results {
    itemIndex: number;
    selected: boolean;
    scheduleId: number;
    scheduleDate: string;
    scheduleDateFormatted: string;
    jobQuoteItemId: number;
    jobId: number;
    siteAddress: string;
    customer: string;
    item: string;
    itemId: number;
    itemShortcut: string;
    type: string;
    typeId: number;
    quantityType: string;
    hirePeriod: string;
    onoffhireDate: string;
    bringForwardDate: string;
    bringForwardDateFormatted: string;
    notes: string;
    isCustomerPickup: boolean;
}

interface ItemOptionList {
    id: number;
    name: string;
    unitOfMeasure: string;
    chargeGst: boolean;
    callout: boolean;
    priceListItem: boolean;
    priceListDefaultDays: number;
    isWalkWay: boolean;
    typeId: number;
    installOnly: boolean;
}

const FlaggedJobsData = (props: FlaggedJobsProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [results, setResults] = React.useState<Results[]>([]);    //original result set, used when searching
    const [filteredResults, setFilteredResults] = React.useState<Results[]>([]);  //result set after searching, used to display and save items
    const [siteAddressSearch, setSiteAddressSearch] = React.useState("");
    const [typeIdSearch, setTypeIdSearch] = React.useState(-1);
    const [itemIdSearch, setItemIdSearch] = React.useState(-1);

    React.useEffect(() => {
        if (props.showModal) {
            getData();
        }
    }, [props.scheduleDate, props.showModal]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        //get list of flagged jobs
        axios.get('api/Schedule/GetFlaggedJobs?SystemCompanyId=' + props.systemCompanyId + '&ScheduleDate=' + props.scheduleDate + '&ScheduleBranchId=' + props.scheduleBranchId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {    
            var jobs = res.data.items;
            var itemIndex = 0;
            for (var i = 0; i < jobs.length; i++) {
                jobs[i].itemIndex = itemIndex;
                itemIndex = + 1;
            }

            setResults(jobs);
            var filtJobs = [...jobs];
            setFilteredResults(filtJobs);
            setItemOptions(res.data.itemOptions);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
        
    }

    const clearError = (e: any) => {
        var error = errors;
        delete error[e.target.name];
        setErrors(error);
    }

    const handleTypeIdChange = (e: any) => {
        clearError(e);

        let value = e.target.value * 1;
        setTypeIdSearch(value);
    }

    const handleItemIdChange = (e: any) => {
        clearError(e);

        let value = e.target.value * 1;
        setItemIdSearch(value);
    }

    const search = () => {
        var searchResults = [...results];

        if (siteAddressSearch) {
            searchResults = searchResults.filter(job => job.siteAddress.toLowerCase().indexOf(siteAddressSearch.toLowerCase()) > -1);
        }

        if (typeIdSearch > 0) {
            searchResults = searchResults.filter(job => job.typeId === typeIdSearch);
        }

        if (itemIdSearch > 0) {
            searchResults = searchResults.filter(job => job.itemId === itemIdSearch);
        }

        setFilteredResults(searchResults);
    }

    const hideModal = () => {
        setSiteAddressSearch("");
        setTypeIdSearch(-1);
        setItemIdSearch(-1);
        setErrors({});
        setFilteredResults(results);    //reset filtered results
        props.hideModal();
    }

    const updateJobItem = (item: Results) => {
        var error = errors;
        delete error["results"];
        setErrors(error);

        var items = [...filteredResults];
        var itemToUpdate = items.find(i => i.scheduleId == item.scheduleId);
        if (itemToUpdate) {
            itemToUpdate = item;
            setFilteredResults(items);
        }
    }

    const validate = () => {
        var items = filteredResults.filter(i => i.selected);
        if (items.length === 0) {
            toast.error("Please select at least one job to Schedule");
            return false;
        }

        let errorList: any = {};
        let formIsValid = true;

        var errorString = "Please enter the following values: ";
        var error = false;

        var row = 0;
        for (var i = 0; i < filteredResults.length; i++) {
            row += 1;
            var newErrorString = "";
            var item = filteredResults[i];

            if (item.selected) {
                //check to make sure on/off hire date set for selected items
                if (!item.onoffhireDate) {
                    newErrorString += "On/Off Hire Date, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(item.onoffhireDate)) {
                    newErrorString += " Valid On/Off Hire Date, ";
                    error = true;
                }

                if (item.type === "Install" && item.onoffhireDate) {
                    //check that Onhire Date is >= Schedule Date
                    if (!DateHelpers.compareDate(props.scheduleDate, item.onoffhireDate)) {
                        newErrorString += " OnHire Date must be >= Scheduled Date, ";
                        error = true;
                    }
                } else if (item.type === "Pick Up" && item.onoffhireDate) {
                    if (!DateHelpers.compareDate(item.onoffhireDate, props.scheduleDate)) {
                        newErrorString += " Offhire Date must be <= Dismantle Date, ";
                        error = true;
                    }
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + row + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
        }
        if (error) {
            formIsValid = false;
            errorList["results"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const schedule = (e: any) => {
        e.preventDefault();
        
        //save flagged job
        if (validate()) {
            flaggedJobsSave();
        }
    }

    const flaggedJobsSave = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var flaggedJobs = filteredResults.filter(f => f.selected);
        var jobsToSave = {
            scheduleDate: props.scheduleDate,
            items: flaggedJobs,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveFlaggedJobs', jobsToSave, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                setSiteAddressSearch("");
                setTypeIdSearch(-1);
                setItemIdSearch(-1);
                setErrors({});
                setFilteredResults(results);

                props.save(res.data.scheduleDates);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    } 

    const renderDetails = (
        <div className="static-modal">
            <Modal size="xl" show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Schedule Job/s for {props.dateFormatted}</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <div className="makeitflexspacebetween alignitemscentre">
                        <label className="input-group--inline" htmlFor="siteAddressSearch">
                            <span className="label label--setsizesm">Site Address</span>
                            <input className="input allocateSiteAddress" type="text" id="siteAddressSearch" name="siteAddressSearch" value={siteAddressSearch} onChange={(e) => { clearError(e); setSiteAddressSearch(e.target.value) } }></input>
                        </label>
                        <label className="input-group--inline" htmlFor="typeIdSearch">
                            <span className="label">Type</span>
                            <select className="select" id="typeIdSearch" name="typeIdSearch" value={typeIdSearch} onChange={(e) => handleTypeIdChange(e) }>
                                <option defaultValue="-1" value={-1}></option>
                                <option key="1" value={1}>Install</option>
                                <option key="2" value={2}>{props.systemCompanyId === 2 ? "Dismantle" : "Pick Up"}</option>
                                <option key="3" value={3}>Assist</option>
                            </select>
                        </label>
                        <label className="input-group--inline" htmlFor="itemIdSearch">
                            <span className="label">Item</span>
                            <select className="select" id="itemIdSearch" name="itemIdSearch" value={itemIdSearch} onChange={(e) => handleItemIdChange(e) }>
                                <option defaultValue="-1" value={-1}></option>
                                {itemOptions?.map(item =>
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                )};
                            </select>
                        </label>
                        <button type="button" className="defaultbutton defaultbutton__small" onClick={search}>Search</button>
                    </div>
                    <JobListData results={filteredResults} systemCompanyId={props.systemCompanyId} errors={errors} update={updateJobItem} filterDate={props.filterDate} />
                </Modal.Body>
                <Modal.Footer>
                    <button className="popup-button--yes" onClick={schedule}>Schedule</button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    let contents = loading
        ? ""
        : renderDetails;

    return (<div>
        {contents}
    </div>)
}

export default FlaggedJobsData;